import React, { useState, useEffect } from 'react';
import { Users, UserPlus, TrendingUp, Calendar } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface DashboardStats {
  uniqueVisitsToday: number;
  newRegistrationsToday: number;
  totalUsers: number;
  verifiedUsers: number;
  usersByRole: Array<{ role_name: string; count: string }>;
  recentRegistrations: Array<{ date: string; count: string }>;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/stats/dashboard');
      // Поддерживаем разные форматы ответа
      if (response.data.success && response.data.stats) {
        setStats(response.data.stats);
      } else if (response.data.stats) {
        setStats(response.data.stats);
      } else if (response.data && response.data.uniqueVisitsToday !== undefined) {
        // Если данные пришли напрямую (без обертки)
        setStats(response.data);
      } else {
        toast.error('Неверный формат данных статистики');
      }
    } catch (error: any) {
      console.error('Dashboard stats error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Ошибка загрузки статистики';
      toast.error(errorMessage);
      // Не устанавливаем stats в null, чтобы показать сообщение об ошибке
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        Ошибка загрузки статистики
      </div>
    );
  }

  const statCards = [
    {
      title: 'Уникальных посещений сегодня',
      value: stats.uniqueVisitsToday,
      icon: TrendingUp,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800'
    },
    {
      title: 'Новых регистраций сегодня',
      value: stats.newRegistrationsToday,
      icon: UserPlus,
      color: 'bg-green-500',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800'
    },
    {
      title: 'Всего пользователей',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-800'
    },
    {
      title: 'Подтвержденных',
      value: stats.verifiedUsers,
      icon: Calendar,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Дашборд</h1>
        <p className="text-gray-600 mt-2">Общая статистика платформы</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className={`${card.bgColor} rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${card.textColor} mb-2`}>
                    {card.title}
                  </p>
                  <p className={`text-3xl font-bold ${card.textColor}`}>
                    {card.value}
                  </p>
                </div>
                <div className={`${card.color} p-3 rounded-full`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Пользователи по ролям</h2>
        <div className="space-y-4">
          {stats.usersByRole.map((role, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-gray-700 font-medium capitalize">{role.role_name}</span>
              </div>
              <span className="text-gray-900 font-bold">{role.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

